import { Link } from 'react-router-dom';
import { usePageMeta, Reveal, APP_STORE_URL } from '../lib/site';
import { CardHeader } from '../components/Bits';
import gpsShot from '../assets/shots/gps-round.jpg';
import friendShot from '../assets/shots/friend-round.jpg';

function Phone({ children, caption, shot, alt, shotH = 989 }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="ph on-pine" style={{ width: 236 }}>
        {shot ? (
          <img src={shot} alt={alt} width="480" height={shotH} style={{ width: '100%', height: 'auto', display: 'block' }} />
        ) : (
          <>
            <div className="ph-notch" />
            <div className="ph-screen" style={{ minHeight: 300 }}>{children}</div>
          </>
        )}
      </div>
      <p style={{ fontSize: '0.88rem', color: 'var(--mut)', marginTop: 14, maxWidth: 236, marginLeft: 'auto', marginRight: 'auto' }}>
        {caption}
      </p>
    </div>
  );
}

export default function Golfers() {
  usePageMeta(
    'For golfers | MyGolfLinks',
    'Book tee times near you, catch Flash Deals straight from the course, and keep every round: GPS distances, scoring, stats, and your crew. Free on the App Store.'
  );

  return (
    <main>
      <section style={{ padding: 'clamp(140px, 16vw, 180px) 0 clamp(48px, 6vw, 72px)' }}>
        <div className="wrap">
          <Reveal>
            <p className="mono" style={{ fontSize: 12.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fairway)', fontWeight: 500, marginBottom: 20 }}>
              For golfers
            </p>
            <h1 className="display" style={{ maxWidth: 800 }}>Deals from the course, not a middleman.</h1>
            <p className="lede" style={{ margin: '24px 0 32px' }}>
              Book tee times at courses near you, catch Flash Deals the moment a course fires one,
              and keep every round: GPS distances, scoring, stats, and the people you play with.
            </p>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <a href={APP_STORE_URL} className="btn btn-gold">Get the app on the App Store</a>
              <span style={{ fontSize: '0.9rem', color: 'var(--mut)' }}>Free on iPhone</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Three screens ── */}
      <section className="band-pine on-pine">
        <div className="wrap" style={{ padding: 'clamp(60px, 8vw, 96px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal className="phone-row">
            <Phone
              shot={gpsShot}
              alt="A live GPS round in the MyGolfLinks app: hole 3, par 5, 450 yards, with three tracked shots drawn on the satellite view and a 295 yard drive"
              caption="Live GPS on the real course: distances, shot tracking, wind, and Enter Score one thumb away."
            />

            <Phone caption="Flash Deals push the moment a course near you fires one. Book in a couple taps.">
              <div className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--cream-faint)', padding: '2px 2px 12px' }}>
                Deals near you
              </div>
              <div className="ph-deal" style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 800, fontSize: '0.84rem' }}>Fair Oaks GC</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--mut)', marginBottom: 6 }}>Today 1p to 3p · 18 with cart</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span className="mono" style={{ fontSize: '1.2rem', fontWeight: 600 }}>$25</span>
                  <s className="mono" style={{ fontSize: '0.78rem', color: 'var(--faint)' }}>$38</s>
                  <span className="mono" style={{ marginLeft: 'auto', fontSize: '0.68rem', fontWeight: 600, color: 'var(--flag)' }}>35% OFF</span>
                </div>
              </div>
              <div className="ph-deal">
                <div style={{ fontWeight: 800, fontSize: '0.84rem' }}>Willow Bend GC</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--mut)', marginBottom: 6 }}>Twilight · walk 9</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span className="mono" style={{ fontSize: '1.2rem', fontWeight: 600 }}>$19</span>
                  <s className="mono" style={{ fontSize: '0.78rem', color: 'var(--faint)' }}>$27</s>
                  <span className="mono" style={{ marginLeft: 'auto', fontSize: '0.68rem', fontWeight: 600, color: 'var(--flag)' }}>30% OFF</span>
                </div>
              </div>
            </Phone>

            <Phone
              shot={friendShot}
              shotH={850}
              alt="Watching a friend's round in the MyGolfLinks app: hole 2 with the shot trail on satellite, fairway hit, green in regulation, one putt, and the full scorecard below"
              caption="Friends' rounds are live. Tap any hole to watch it, right down to fairways hit and putts."
            />
          </Reveal>
        </div>
      </section>

      {/* ── What's in it ── */}
      <section>
        <div className="wrap" style={{ padding: 'clamp(64px, 8vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="01" label="What's in the app" />
            <h2 className="h2" style={{ maxWidth: 620 }}>The whole golf life, one app</h2>
          </Reveal>
          <Reveal className="ledger" style={{ marginTop: 40 }}>
            <div className="led">
              <span className="led-no">1</span>
              <span className="led-title">Tee times and deals</span>
              <span className="led-body">
                Real courses near you, real rates, and Flash Deals straight from the course. One
                flat $1.80 booking fee, shown before you pay. No surprise fees stacked at checkout.
              </span>
            </div>
            <div className="led">
              <span className="led-no">2</span>
              <span className="led-title">GPS rounds, scoring, stats</span>
              <span className="led-body">
                Distances to front, center, and back on every hole, scoring as you walk, and a stat
                line that follows you round to round.
              </span>
            </div>
            <div className="led">
              <span className="led-no">3</span>
              <span className="led-title">Your crew</span>
              <span className="led-body">
                A feed with your friends' rounds, groups for the regular game, and scorecards that
                remember who was there.
              </span>
            </div>
            <div className="led">
              <span className="led-no">4</span>
              <span className="led-title">The shop</span>
              <span className="led-body">
                Deals from golf brands, in the same app your rounds live in.
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="band-pine on-pine">
        <div className="wrap" style={{ padding: 'clamp(64px, 9vw, 110px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal style={{ maxWidth: 640 }}>
            <h2 className="h2">Free. On your phone before your next round.</h2>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 28 }}>
              <a href={APP_STORE_URL} className="btn btn-gold">Get the app on the App Store</a>
            </div>
            <p style={{ marginTop: 22, fontSize: '0.92rem', color: 'var(--cream-mut)' }}>
              Run a course? <Link to="/courses" className="tlink">See what LinksOS does.</Link>
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
